import type Order from '~/types/Order';
import type ParcelTemplate from '~/types/ParcelTemplate';

import { DistanceUnit } from '~/constants/distance';
import { WeightUnit } from '~/constants/weight';
import { getLineItemsTotalWeight } from '~/types/LineItem';
import { getOrderWeight } from '~/types/Order';
import {
  getBatchEntryLineItemsTotalWeight,
  type BatchEntryExtended,
} from '~/types/OrderBatchEntry';
import { getWeightString } from '~/types/ParcelTemplate';

import {
  type BlackbeardDistanceUnit,
  type BlackbeardParcelTemplate,
  type BlackbeardWeightUnit,
} from '../LabelPurchase/modules/types';
import { type OrdersSearchQueryParameters } from './types';

export const getRegExpForQueryParameter = (parameter: string) =>
  new RegExp(`^${parameter}(\\[\\d+\\])?$`);

// getOrderLineItemWeightString gets the weight string from the partial fulfillment or order
export const getOrderLineItemWeightString = (
  order: Order,
  batchEntry?: BatchEntryExtended,
): string | undefined => {
  const batchEntryLineItems = batchEntry?.lineItems || [];

  // Order weight, line item weight, or partial fulfillment line item weight
  const partialFulfillmentRequestLineItemsWeight = batchEntry
    ? getBatchEntryLineItemsTotalWeight(batchEntryLineItems, order.lineItems)
    : null;

  const weight = (
    partialFulfillmentRequestLineItemsWeight ||
    getOrderWeight(order) ||
    getLineItemsTotalWeight(order.lineItems)
  ).toString();

  const weightUnit = order.weightUnit || order.lineItems[0]?.weightUnit;
  if (weight && weightUnit) return getWeightString({ weight, weightUnit });
};

// getParcelWeightString gets the weight string from the parcel
export const getParcelWeightString = (parcel?: {
  weight: string;
  weightUnit: string;
}): string | undefined => {
  if (!!parcel && parcel.weight && parcel.weightUnit) return getWeightString(parcel);
};

// The item filter requires extra conversion to the search query for it to
// work correctly. It comes as separate items with consecutive indices
// (eg: item[0]: something, item[1]: something else ) but it is converted into an array here
export const searchQueryToSearchParameter = (
  searchQuery: OrdersSearchQueryParameters,
  parameter: string,
) =>
  Object.entries(searchQuery)
    .filter(([key]) => getRegExpForQueryParameter(parameter).test(key))
    .reduce<string[]>((accum, [, value]) => {
      if (value) accum.push(value);

      return accum;
    }, []);

// Since the items filter come as an array, this conversion function
// turns it back into its original form
export const searchParameterToSearchQuery = (
  filterValue: string[],
  queryParams: OrdersSearchQueryParameters,
  parameter: string,
) => {
  filterValue.forEach((item, index) => {
    queryParams[`${parameter}[${index}]`] = item;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const areEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

const distanceUnitBlackbeardToFeron = (distanceUnit: BlackbeardDistanceUnit): DistanceUnit => {
  switch (distanceUnit) {
    case 'cm': {
      return DistanceUnit.CM;
    }
    case 'in': {
      return DistanceUnit.IN;
    }
    case 'mm': {
      return DistanceUnit.MM;
    }
  }
};

export const weightUnitBlackbeardToFeron = (weightUnit?: BlackbeardWeightUnit): WeightUnit => {
  switch (weightUnit) {
    case 'g': {
      return WeightUnit.G;
    }
    case 'kg': {
      return WeightUnit.KG;
    }
    case 'lb': {
      return WeightUnit.LB;
    }
    case 'oz': {
      return WeightUnit.OZ;
    }
    default: {
      return WeightUnit.LB;
    }
  }
};

export const parcelTemplateBlackbeardToFeron = (
  blackbeardParcelTemplate: BlackbeardParcelTemplate,
): ParcelTemplate => ({
  ...blackbeardParcelTemplate,
  carrierTemplate: blackbeardParcelTemplate.carrierTemplate
    ? {
        ...blackbeardParcelTemplate.carrierTemplate,
        distanceUnit: distanceUnitBlackbeardToFeron(
          blackbeardParcelTemplate.carrierTemplate?.distanceUnit,
        ),
      }
    : undefined,
  distanceUnit: distanceUnitBlackbeardToFeron(blackbeardParcelTemplate.distanceUnit),
  weight: blackbeardParcelTemplate.weight || 0,
  weightUnit: weightUnitBlackbeardToFeron(blackbeardParcelTemplate.weightUnit),
});
